import type { BingoCard } from '@/lib/types';

interface Props {
    card: BingoCard;
    drawn: Set<number>;
    highlight?: boolean;
    /**
     * Player's own manual marks. When provided (together with onToggleMark),
     * the card is interactive and a number is only shown as marked once the
     * player taps it themselves -- no auto-daub. Omit both for a static
     * display (e.g. showing someone else's winning card after the round
     * ends), which falls back to the old drawn-derived marking.
     */
    marked?: Set<number>;
    onToggleMark?: (n: number) => void;
}

const COLUMN_ORDER: Array<keyof BingoCard> = ['B', 'I', 'N', 'G', 'O'];
const LETTER_CLASS: Record<keyof BingoCard, string> = {
    B: 'col-b',
    I: 'col-i',
    N: 'col-n',
    G: 'col-g',
    O: 'col-o',
};

export function BingoCardView({ card, drawn, highlight = false, marked, onToggleMark }: Props) {
    const interactive = marked !== undefined && onToggleMark !== undefined;

    return (
        <table className="bingo-table">
            <thead>
                <tr>
                    {COLUMN_ORDER.map((c) => (
                        <th key={c} className={LETTER_CLASS[c]}>
                            {c}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {[0, 1, 2, 3, 4].map((row) => (
                    <tr key={row}>
                        {COLUMN_ORDER.map((col) => {
                            const n = card[col][row];
                            const isFree = n === 0;
                            const isMarked = isFree || (interactive ? marked.has(n) : drawn.has(n));
                            const isCalled = drawn.has(n);
                            const cls = [
                                'bingo-cell',
                                isMarked ? 'marked' : '',
                                highlight ? 'won' : '',
                                isFree ? 'free' : '',
                                interactive && !isFree ? 'daubable' : '',
                            ]
                                .filter(Boolean)
                                .join(' ');
                            const content = isFree ? '*' : n;

                            return (
                                <td key={col}>
                                    {interactive && !isFree ? (
                                        <button
                                            type="button"
                                            className={cls}
                                            disabled={!isCalled}
                                            onClick={() => onToggleMark(n)}
                                            aria-pressed={isMarked}
                                            aria-label={`Mark ${n}`}
                                        >
                                            {content}
                                        </button>
                                    ) : (
                                        <div className={cls}>{content}</div>
                                    )}
                                </td>
                            );
                        })}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}
